import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, Heading, Separator } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { EnvelopeAccountRow } from './components/envelope-account-row/EnvelopeAccountRow';
import { Five29AccountRow } from './components/envelope-account-row/Five29AccountRow';
import { ExternalAccountRow } from './components/ExternalAccountRow';
import { PurposeAccountsCardDocument } from './graphql/purposeAccountsCard.generated';

import { EnvelopeAccountPerformanceHistoryDocument } from '../../../account/components/overview-card/graphql/envelopeAccountPerformanceHistory.generated';
import { DateRangeOption } from '../../../graphql/schema';
import { Prefetch } from '../../../Shared/components-v2/prefetch/Prefetch';
import { urlBuilder } from '../../../Shared/urlBuilder';

export const AccountsCard: React.FC = () => {
  const purposeId = useRequiredParam('purposeId');
  const { data } = useGraphQLQuery(PurposeAccountsCardDocument, { variables: { id: purposeId }, throwOnError: true });

  if (!data) return <CardLoader />;
  const { purpose } = data;
  const { legacyGoalId } = purpose;
  const { accounts, hasRecurringTransaction } = purpose.envelope;

  const managedAccount = accounts.length === 1 && accounts[0].__typename === 'ManagedAccount' ? accounts[0] : null;
  const isEligibleForTcpCoordination = managedAccount?.isTaxCoordinationEligible && !managedAccount?.isTaxCoordinated;
  const isTaxCoordinated =
    accounts.length > 1 && accounts[0].__typename === 'ManagedAccount' && accounts[0].isTaxCoordinated;

  const queries = accounts.map(account => ({
    query: EnvelopeAccountPerformanceHistoryDocument,
    variables: { id: account.id, dateRangeOption: DateRangeOption.AllTime },
    uniqueKey: `EnvelopeAccountPerformanceHistory-${account.id}`,
  }));

  return (
    <Prefetch queries={queries}>
      <Card>
        <Card.Header css={{ padding: 'space.300' }}>
          <Heading as="h2" variant="title200">
            Accounts
          </Heading>
          {isEligibleForTcpCoordination && (
            <SambaAutoLink to={urlBuilder.getTaxLocationSetup.absolute()}>Set up Tax Coordination</SambaAutoLink>
          )}
        </Card.Header>
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <Card.Content>
          {accounts.map(a => {
            switch (a.__typename) {
              case 'Five29Account':
                return <Five29AccountRow key={a.id} account={a} />;
              case 'CashAccount':
              case 'ManagedAccount':
                return (
                  <EnvelopeAccountRow
                    key={a.id}
                    account={a}
                    hasRecurringTransaction={accounts.length === 1 && hasRecurringTransaction}
                  />
                );
              case 'ExternalAccountEnvelopeAssociation':
                return <ExternalAccountRow key={a.id} account={a.account} />;
              case 'CheckingAccount':
              default:
                return null;
            }
          })}
        </Card.Content>
        <Card.Footer css={{ padding: 'space.300', display: 'flex' }}>
          {isTaxCoordinated && (
            <Box>
              <SambaAutoLink to={urlBuilder.addGoal.absolute()}>Add account</SambaAutoLink>
              &nbsp;|&nbsp;
            </Box>
          )}

          {legacyGoalId ? (
            <SambaAutoLink
              to={urlBuilder.appExternalAccountAssociation.absolute({
                goalId: legacyGoalId,
                return_to: `${urlBuilder.appPurpose.absolute({ purposeId: purpose.id })}`,
              })}
            >
              Link external account
            </SambaAutoLink>
          ) : (
            <SambaAutoLink to={urlBuilder.appExternalAccountSetup.absolute()}>Link external account</SambaAutoLink>
          )}
        </Card.Footer>
      </Card>
    </Prefetch>
  );
};
